import { RADIUS, SHADOW, WithHalfdot } from "@elyxeer/ui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { LoginScreen } from "~/components/auth/LoginScreen";
import { ApiError, fetchMembershipInvite } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import { useAuthStore } from "~/store/auth-store";

interface InviteStateProps {
  title: string;
  body: string;
  testId: string;
  alert?: "error" | "success";
  children?: ReactNode;
}

function InviteState({
  title,
  body,
  testId,
  alert,
  children,
}: InviteStateProps) {
  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        backgroundColor: "background.default",
        paddingInline: 2,
        paddingTop: "calc(24px + env(safe-area-inset-top))",
        paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
      }}
    >
      <Paper
        variant="outlined"
        data-testid={testId}
        sx={{
          width: "100%",
          maxWidth: 480,
          padding: { xs: 3, md: 4 },
          borderRadius: `${RADIUS.xl}px`,
          boxShadow: SHADOW.raised,
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{title}</WithHalfdot>
          </Typography>
          {alert ? (
            <Alert severity={alert} sx={{ marginTop: 2 }}>
              {body}
            </Alert>
          ) : (
            <Typography
              variant="T2"
              sx={{ color: "text.secondary", marginTop: 1.5 }}
            >
              {body}
            </Typography>
          )}
        </Box>
        {children}
      </Paper>
    </Box>
  );
}

/** Public invitation landing and authenticated explicit-claim screen. */
export function InviteRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token = "" } = useParams();
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);
  const claimInvite = useAuthStore((state) => state.claimInvite);
  const signOut = useAuthStore((state) => state.signOut);
  const [claiming, setClaiming] = useState(false);
  const [claimErrorKey, setClaimErrorKey] = useState<string | null>(null);

  const preview = useQuery({
    queryKey: queryKeys.membershipInvite(token),
    queryFn: ({ signal }) => fetchMembershipInvite(token, signal),
    enabled: token.length > 0,
    staleTime: 0,
    refetchOnMount: "always",
  });

  if (!token) {
    return (
      <InviteState
        title={t("invite.unavailableTitle")}
        body={t("invite.unavailableBody")}
        testId="invite.unavailable"
        alert="error"
      >
        <Button variant="contained" onClick={() => navigate("/auth")}>
          {t("invite.regularSignIn")}
        </Button>
      </InviteState>
    );
  }

  if (preview.isPending) {
    return (
      <Box
        data-testid="invite.loading"
        sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}
      >
        <CircularProgress size={28} thickness={4} />
      </Box>
    );
  }

  const unavailable =
    preview.error instanceof ApiError &&
    (preview.error.status === 404 || preview.error.status === 410);
  if (unavailable) {
    return (
      <InviteState
        title={t("invite.unavailableTitle")}
        body={t("invite.unavailableBody")}
        testId="invite.unavailable"
        alert="error"
      >
        <Button variant="contained" onClick={() => navigate("/auth")}>
          {t("invite.regularSignIn")}
        </Button>
      </InviteState>
    );
  }

  if (preview.isError || !preview.data) {
    return (
      <InviteState
        title={t("invite.errorTitle")}
        body={t("invite.errorBody")}
        testId="invite.error"
        alert="error"
      >
        <Button variant="contained" onClick={() => void preview.refetch()}>
          {t("common.retry")}
        </Button>
      </InviteState>
    );
  }

  const invite = preview.data;
  if (invite.status === "revoked") {
    return (
      <InviteState
        title={t("invite.unavailableTitle")}
        body={t("invite.unavailableBody")}
        testId="invite.unavailable"
        alert="error"
      >
        <Button variant="contained" onClick={() => navigate("/auth")}>
          {t("invite.regularSignIn")}
        </Button>
      </InviteState>
    );
  }

  if (status === "unauthenticated") {
    return (
      <LoginScreen
        key={token}
        initialEmail={invite.email}
        emailReadOnly
        returnTo={`/invite/${token}`}
        inviteToken={token}
        inviteStatus={invite.status}
      />
    );
  }

  if (!user) return null;

  const matchingEmail =
    user.email.trim().toLocaleLowerCase() ===
    invite.email.trim().toLocaleLowerCase();

  if (matchingEmail && user.has_membership_override) {
    return (
      <InviteState
        title={t("invite.successTitle")}
        body={t("invite.successBody")}
        testId="invite.success"
        alert="success"
      >
        <Button
          variant="contained"
          onClick={() => navigate("/news", { replace: true })}
        >
          {t("invite.enterApp")}
        </Button>
      </InviteState>
    );
  }

  if (invite.status !== "pending") {
    return (
      <InviteState
        title={t("invite.claimedTitle")}
        body={t("invite.claimedBody")}
        testId="invite.unavailable"
        alert="error"
      >
        <Button
          variant="contained"
          onClick={() => navigate("/news", { replace: true })}
        >
          {t("invite.enterApp")}
        </Button>
      </InviteState>
    );
  }

  if (!matchingEmail) {
    return (
      <InviteState
        title={t("invite.emailMismatchTitle")}
        body={t("invite.emailMismatchBody", {
          invitedEmail: invite.email,
          signedInEmail: user.email,
        })}
        testId="invite.email-mismatch"
        alert="error"
      >
        <Button variant="contained" onClick={() => void signOut()}>
          {t("invite.useDifferentAccount")}
        </Button>
      </InviteState>
    );
  }

  const handleClaim = async () => {
    setClaiming(true);
    setClaimErrorKey(null);
    try {
      await claimInvite(token);
    } catch (error) {
      if (
        error instanceof ApiError &&
        (error.status === 404 || error.status === 409 || error.status === 410)
      ) {
        await preview.refetch();
      }
      setClaimErrorKey("invite.claimError");
    } finally {
      setClaiming(false);
    }
  };

  return (
    <InviteState
      title={t("invite.readyTitle")}
      body={t("invite.readyBody", { email: invite.email })}
      testId="invite.ready"
    >
      {claimErrorKey ? (
        <Alert severity="error">{t(claimErrorKey)}</Alert>
      ) : null}
      <Button
        variant="contained"
        disabled={claiming}
        onClick={() => void handleClaim()}
        data-testid="invite.claim"
      >
        {t("invite.claim")}
      </Button>
    </InviteState>
  );
}
